/** Tiny DOM helpers. Not a framework — just less `document.createElement`. */

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

export function clear(node: Element): void {
  while (node.firstChild) node.removeChild(node.firstChild);
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0] as string).slice(0, 2).toUpperCase();
  return `${(parts[0] as string)[0]}${(parts[1] as string)[0]}`.toUpperCase();
}

/**
 * Long-press without swallowing taps or scrolls: fires only if the finger stays
 * put for `delay`, and cancels the moment it moves or lifts.
 */
export function onLongPress(target: HTMLElement, run: () => void, delay = 420): void {
  let timer: number | null = null;
  let startX = 0;
  let startY = 0;

  const cancel = (): void => {
    if (timer !== null) window.clearTimeout(timer);
    timer = null;
  };

  target.addEventListener('pointerdown', (event) => {
    startX = event.clientX;
    startY = event.clientY;
    cancel();
    timer = window.setTimeout(() => {
      timer = null;
      run();
    }, delay);
  });

  target.addEventListener('pointermove', (event) => {
    if (timer === null) return;
    if (Math.hypot(event.clientX - startX, event.clientY - startY) > 12) cancel();
  });

  for (const type of ['pointerup', 'pointercancel', 'pointerleave'] as const) {
    target.addEventListener(type, cancel);
  }
}
